import type {
  IDraftAddonGroup,
  IDraftCategory,
  IDraftDelta,
  IDraftItem,
  IMenuDraft,
  TDraftQuestion,
} from '@/interfaces/IMenuImport';
import { isNewDish, isUpdateDraft, liveItemState } from './live-update';

/**
 * Pure helpers over the menu draft of the AI menu import (plan §10.3). The
 * server owns every rule that changes the draft (ops, review levels); these
 * functions only apply the deltas it sends and read the result for the UI.
 */

// ---------------------------------------------------------------------------
// Deltas
// ---------------------------------------------------------------------------

/** A copy of `current` with the removals and upserts applied; `current` itself when nothing changes. */
function mergeRecords<T extends { id: string }>(
  current: Record<string, T>,
  upserts: T[] | undefined,
  removed: string[] | undefined
): Record<string, T> {
  if (!upserts?.length && !removed?.length) return current;
  const next = { ...current };
  for (const id of removed ?? []) delete next[id];
  for (const record of upserts ?? []) next[record.id] = record;
  return next;
}

/**
 * The delta changes the draft. A write that changed nothing answers only the
 * server's current `rev`, `canUndo` and `status`; a client must never take
 * that answer for the changes of that rev (§5.4 client rule).
 */
export function deltaHasChanges(delta: IDraftDelta): boolean {
  const removed = delta.removed;
  return (
    !!delta.categories?.length ||
    !!delta.items?.length ||
    !!delta.addonGroups?.length ||
    !!delta.questions?.length ||
    !!removed?.categories?.length ||
    !!removed?.items?.length ||
    !!removed?.addonGroups?.length ||
    !!removed?.questions?.length ||
    delta.categoryOrder !== undefined ||
    delta.questionOrder !== undefined ||
    delta.meta !== undefined ||
    delta.live !== undefined ||
    delta.liveHiddenIds !== undefined
  );
}

/** The live block after a delta: replaced, removed (`null`), or only its hides changed. */
function nextLive(draft: IMenuDraft, delta: IDraftDelta): IMenuDraft['live'] {
  if (delta.live === null) return undefined;
  const live = delta.live ?? draft.live;
  if (live && delta.liveHiddenIds !== undefined)
    return { ...live, hiddenIds: delta.liveHiddenIds };
  return live;
}

/**
 * The draft after a server delta (§5.4). Always a new draft object; only the
 * maps the delta touches are copied and each touched record is replaced, so
 * unchanged dishes keep their references (Vue 2 then skips re-rendering them).
 */
export function applyDraftDelta(
  draft: IMenuDraft,
  delta: IDraftDelta
): IMenuDraft {
  const removed = delta.removed ?? {};
  const next: IMenuDraft = {
    ...draft,
    languageId: delta.meta ? delta.meta.languageId : draft.languageId,
    currency: delta.meta ? delta.meta.currency : draft.currency,
    categoryOrder: delta.categoryOrder ?? draft.categoryOrder,
    questionOrder: delta.questionOrder ?? draft.questionOrder,
    categories: mergeRecords(
      draft.categories,
      delta.categories,
      removed.categories
    ),
    items: mergeRecords(draft.items, delta.items, removed.items),
    addonGroups: mergeRecords(
      draft.addonGroups,
      delta.addonGroups,
      removed.addonGroups
    ),
    questions: mergeRecords(
      draft.questions,
      delta.questions,
      removed.questions
    ),
  };
  const live = nextLive(draft, delta);
  if (live) next.live = live;
  else delete next.live;
  return next;
}

// ---------------------------------------------------------------------------
// Reading the draft
// ---------------------------------------------------------------------------

function isDefined<T>(value: T | undefined): value is T {
  return value !== undefined;
}

/** True when the draft holds at least one dish (the API's `review`/`collecting` status rule, §8.5). */
export function draftHasItems(draft: IMenuDraft | null): boolean {
  return !!draft && Object.keys(draft.items).length > 0;
}

/** Categories in menu order. */
export function orderedCategories(draft: IMenuDraft): IDraftCategory[] {
  return draft.categoryOrder
    .map((id) => draft.categories[id])
    .filter(isDefined);
}

/** The dishes of one category, in its order. */
export function categoryItems(
  draft: IMenuDraft,
  category: IDraftCategory
): IDraftItem[] {
  return category.itemIds.map((id) => draft.items[id]).filter(isDefined);
}

/** Every dish in menu order (categories in order, then each category's dishes). */
export function orderedItems(draft: IMenuDraft): IDraftItem[] {
  return orderedCategories(draft).flatMap((category) =>
    categoryItems(draft, category)
  );
}

/** A dish has a price: its single price, or every one of its sizes (review rule 2, §8.11). */
export function itemIsPriced(item: IDraftItem): boolean {
  if (item.sizes.length > 0)
    return item.sizes.every((size) => size.price !== null);
  return item.price !== null;
}

/**
 * Visible dishes without a price: what «Ocultar {count} sin precio» hides.
 * Only dishes publishing adds (a dish already on the menu keeps its price
 * when the photo has none).
 */
export function unpricedVisibleItemIds(draft: IMenuDraft): string[] {
  return orderedItems(draft)
    .filter((item) => isNewDish(item) && !item.hidden && !itemIsPriced(item))
    .map((item) => item.id);
}

function openBlockingQuestions(draft: IMenuDraft): TDraftQuestion[] {
  return draft.questionOrder
    .map((id) => draft.questions[id])
    .filter(isDefined)
    .filter((question) => question.status === 'open' && question.blocking);
}

export interface IDraftSummary {
  /** Every dish of the draft. */
  items: number;
  categories: number;
  /** Dishes to check: amber and red. */
  toCheck: number;
  /** Red dishes. */
  fix: number;
  /** Red because the price is missing (new dishes). */
  missingPrices: number;
  openBlockingQuestions: number;
  /** Visible new dishes without a price. */
  unpricedVisible: number;
  /** Hidden new dishes without a price. */
  hiddenUnpriced: number;
  /** New dishes without a photo. */
  noPhoto: number;
  /** Some new dish has allergens. */
  allergensAny: boolean;
  /** New dishes whose allergens are not confirmed (they are published without them). */
  allergensUnconfirmed: number;
  /** Dishes the diners will see with a price (the API's `visiblePricedCount`, §8.11). */
  visiblePriced: number;
}

/**
 * The draft numbers of the summary card, the bar and the publish dialog.
 * Price, photo and allergen counts cover only the dishes publishing adds: a
 * dish already on the menu (an update session) keeps its own; every dish of
 * other sessions is new.
 */
export function draftSummary(draft: IMenuDraft): IDraftSummary {
  const categories = orderedCategories(draft);
  const summary: IDraftSummary = {
    items: 0,
    categories: categories.length,
    toCheck: 0,
    fix: 0,
    missingPrices: 0,
    openBlockingQuestions: openBlockingQuestions(draft).length,
    unpricedVisible: 0,
    hiddenUnpriced: 0,
    noPhoto: 0,
    allergensAny: false,
    allergensUnconfirmed: 0,
    visiblePriced: 0,
  };

  for (const category of categories) {
    for (const item of categoryItems(draft, category)) {
      const priced = itemIsPriced(item);
      summary.items++;
      if (item.review.level !== 'ok') summary.toCheck++;
      if (item.review.level === 'fix') summary.fix++;
      if (priced && !item.hidden && !category.hidden) summary.visiblePriced++;
      if (!isNewDish(item)) continue;
      if (item.review.level === 'fix' && item.review.reason === 'missing_price')
        summary.missingPrices++;
      if (!priced && !item.hidden) summary.unpricedVisible++;
      if (!priced && item.hidden) summary.hiddenUnpriced++;
      if (!item.photo) summary.noPhoto++;
      if (item.allergens !== 0) {
        summary.allergensAny = true;
        if (!item.allergensConfirmed) summary.allergensUnconfirmed++;
      }
    }
  }

  return summary;
}

// ---------------------------------------------------------------------------
// Filters
// ---------------------------------------------------------------------------

export type TItemFilter =
  | 'all'
  | 'toCheck'
  | 'noPrice'
  | 'noPhoto'
  | 'priceChanged'
  | 'new'
  | 'unchanged';

/** The chips of a session that is not an update. */
export const ITEM_FILTERS: readonly TItemFilter[] = [
  'all',
  'toCheck',
  'noPrice',
  'noPhoto',
];

const ALL_FILTERS: readonly TItemFilter[] = [
  ...ITEM_FILTERS,
  'priceChanged',
  'new',
  'unchanged',
];

const FILTER_PREDICATES: Record<TItemFilter, (item: IDraftItem) => boolean> = {
  all: () => true,
  toCheck: (item) => item.review.level !== 'ok',
  // A dish already on the menu keeps its price when the photo has none.
  noPrice: (item) => isNewDish(item) && !itemIsPriced(item),
  noPhoto: (item) => !item.photo,
  priceChanged: (item) => liveItemState(item) === 'priceChanged',
  new: (item) => {
    const state = liveItemState(item);
    return state === 'new' || state === 'suggested';
  },
  unchanged: (item) => liveItemState(item) === 'unchanged',
};

export function isItemFilter(value: unknown): value is TItemFilter {
  return typeof value === 'string' && ALL_FILTERS.some((f) => f === value);
}

/**
 * The chips of the filter bar (plan §3.5): update sessions show «Precio
 * nuevo», «Nuevos» and «Sin cambios», and «Sin precio» only while it counts a
 * dish or is the active chip; other sessions keep their chips.
 */
export function itemFiltersFor(
  draft: IMenuDraft,
  active: TItemFilter
): TItemFilter[] {
  if (!isUpdateDraft(draft)) return [...ITEM_FILTERS];
  const filters: TItemFilter[] = [
    'all',
    'toCheck',
    'priceChanged',
    'new',
    'unchanged',
  ];
  if (active === 'noPrice' || filterCounts(draft).noPrice > 0)
    filters.push('noPrice');
  return filters;
}

/** Whether a filter chip shows the dish (also for dishes a proposal would add). */
export function matchesItemFilter(
  item: IDraftItem,
  filter: TItemFilter
): boolean {
  return FILTER_PREDICATES[filter](item);
}

/** The dishes a filter chip shows, in menu order. */
export function filterItems(
  draft: IMenuDraft,
  filter: TItemFilter
): IDraftItem[] {
  return orderedItems(draft).filter(FILTER_PREDICATES[filter]);
}

/** The count of every filter chip. */
export function filterCounts(draft: IMenuDraft): Record<TItemFilter, number> {
  const counts: Record<TItemFilter, number> = {
    all: 0,
    toCheck: 0,
    noPrice: 0,
    noPhoto: 0,
    priceChanged: 0,
    new: 0,
    unchanged: 0,
  };
  for (const item of orderedItems(draft)) {
    for (const filter of ALL_FILTERS) {
      if (FILTER_PREDICATES[filter](item)) counts[filter]++;
    }
  }
  return counts;
}

export interface IDraftSection {
  category: IDraftCategory;
  items: IDraftItem[];
}

/**
 * The list the workspace renders: categories in order with their filtered
 * dishes. «Todos» keeps empty categories (manual building adds dishes to
 * them); the other filters keep only the categories with a match.
 */
export function draftSections(
  draft: IMenuDraft,
  filter: TItemFilter
): IDraftSection[] {
  const sections: IDraftSection[] = [];
  for (const category of orderedCategories(draft)) {
    const items = categoryItems(draft, category).filter(
      FILTER_PREDICATES[filter]
    );
    if (filter === 'all' || items.length > 0)
      sections.push({ category, items });
  }
  return sections;
}

// ---------------------------------------------------------------------------
// Review queue
// ---------------------------------------------------------------------------

export type TReviewQueueEntry =
  | { kind: 'question'; id: string }
  | { kind: 'item'; id: string };

/**
 * «Revisar uno por uno» (§4.5.4): open blocking questions first, then red
 * dishes, then amber dishes, in menu order. A dish that is red only because
 * a queued question lists it is left to that question's card.
 */
export function reviewQueue(draft: IMenuDraft): TReviewQueueEntry[] {
  const questions = openBlockingQuestions(draft);
  const answeredByQuestion = new Set(
    questions.flatMap((question) => question.itemIds)
  );
  const items = orderedItems(draft);
  const red = items.filter(
    (item) =>
      item.review.level === 'fix' &&
      !(item.review.reason === 'question' && answeredByQuestion.has(item.id))
  );
  const amber = items.filter((item) => item.review.level === 'check');

  return [
    ...questions.map(
      (question): TReviewQueueEntry => ({
        kind: 'question',
        id: question.id,
      })
    ),
    ...[...red, ...amber].map(
      (item): TReviewQueueEntry => ({
        kind: 'item',
        id: item.id,
      })
    ),
  ];
}

/**
 * The red dish «Publicar menú» opens: the first one missing a price (the
 * owner can fix it in its row), else the first red dish of any reason.
 */
export function firstRedItemId(draft: IMenuDraft): string | null {
  const red = orderedItems(draft).filter((item) => item.review.level === 'fix');
  const missingPrice = red.find(
    (item) => item.review.reason === 'missing_price'
  );
  return (missingPrice ?? red[0])?.id ?? null;
}

// ---------------------------------------------------------------------------
// Add-on group rule
// ---------------------------------------------------------------------------

export interface IAddonRuleText {
  key: string;
  params: Record<string, number>;
}

export type TAddonRuleInput = Pick<
  IDraftAddonGroup,
  'kind' | 'required' | 'multiple' | 'min' | 'max'
>;

/** The plain-words rule of an add-on group («elija de 1 a 2», «extras, hasta 3»). */
export function addonRuleKey(group: TAddonRuleInput): IAddonRuleText {
  const min = group.min !== null && group.min >= 1 ? group.min : null;
  const max = group.max !== null && group.max >= 1 ? group.max : null;

  if (group.kind === 'add') {
    return max !== null
      ? { key: 'menuImport.rule.extrasMax', params: { max } }
      : { key: 'menuImport.rule.extrasAny', params: {} };
  }

  if (!group.multiple) {
    return group.required
      ? { key: 'menuImport.rule.pickOneRequired', params: {} }
      : { key: 'menuImport.rule.pickOneOptional', params: {} };
  }

  if (min !== null && max !== null)
    return { key: 'menuImport.rule.pickManyRange', params: { min, max } };
  if (max !== null)
    return { key: 'menuImport.rule.pickManyMax', params: { max } };
  if (min !== null)
    return { key: 'menuImport.rule.pickManyMin', params: { min } };
  return { key: 'menuImport.rule.pickManyAny', params: {} };
}
